import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode
} from "react";
import { Button, type ButtonVariant } from "./Button.js";

/**
 * Split button (Design.md §9): botão de ação primária + dropdown. Usado em
 * Salvar (Gerenciar envios) e Visualizar / Enviar novamente (tabela §5.4).
 */
export interface SplitButtonItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface SplitButtonProps {
  label: ReactNode;
  onClick: () => void;
  items: readonly SplitButtonItem[];
  variant?: ButtonVariant;
  style?: CSSProperties;
}

export function SplitButton({
  label,
  onClick,
  items,
  variant = "primary",
  style
}: SplitButtonProps): ReactNode {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onDocClick(event: MouseEvent): void {
      const node = containerRef.current;
      if (node !== null && !node.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-flex", ...(style ?? {}) }}
    >
      <Button
        variant={variant}
        onClick={onClick}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      >
        {label}
      </Button>
      <Button
        variant={variant}
        aria-label="Mais ações"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          marginLeft: 1,
          padding: "0.5rem 0.6rem"
        }}
      >
        ▾
      </Button>
      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 180,
            background: "var(--panel)",
            border: "1px solid var(--border)",
            borderRadius: 9,
            boxShadow: "var(--shadow-lg)",
            padding: "0.25rem",
            zIndex: 50
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled === true}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                color: "var(--text)",
                fontWeight: 500,
                padding: "0.45rem 0.6rem",
                cursor: item.disabled === true ? "not-allowed" : "pointer"
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
